// TIP-493 (MV Stage 4b) — "Buy a Web Profile" payment step (step 2 of 3).
// Header + Stripe payment (left) and the persistent order summary (right).
import { PaymentDetailsForm } from "./PaymentsDetailsForm";
import { WebProfileSummary } from "../WebProfileSummary";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setUserInfo } from "../../../../store/auth/authSlice";
import { useGetUserQuery } from "../../../../store/auth/usersApiSlice";
import { useGetCompanyQuery } from "../../../../store/Company/companyApiSlice";
import { buildWebProfileSummary, PROFILE_WINDOW_DAYS } from "../../../../constants/webProfilePricing";
import Header from "../../Header";
import Footer from "../../Footer";
import { useEffect, useState } from "react";
import { CommonToast } from "../../../common/Toast";

export const PaymentDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [paymentBreakdown, setPaymentBreakdown] = useState();
  const { planDetails } = location.state || {};
  const dispatch = useDispatch();
  const { token } = useSelector((state) => state.auth);
  const { data, isSuccess, isError, error } = useGetUserQuery(token);

  // TIP-493 (B3) — survive a page reload / deep-load: fall back to localStorage
  // for the target company and the chosen mode (route state is lost on refresh).
  const storedCompanyId = (() => {
    try { return JSON.parse(localStorage.getItem("payasyougoCompanyID")); } catch (e) { return null; }
  })();
  const company_id = location.state?.company_id || storedCompanyId;
  // TIP-556 — ordering a report for a company NOT yet on TREVEX: there is no
  // company_id (the webhook creates the company after payment). The route
  // state flag survives refresh; the localStorage flag (set by CreateCompany
  // right before navigating here) covers deep-loads without state.
  // TIP-564 — only trust the localStorage flag when there is NO route state
  // at all (a true deep-load). Any in-app navigate() call — even one for an
  // unrelated purchase that doesn't set reportRequest — passes its own state
  // object, so it must not fall back to a stale flag left by an abandoned
  // report order.
  const isReportRequest = location.state
    ? Boolean(location.state.reportRequest)
    : localStorage.getItem("createWorldboxCompany") === "true";
  const requestedCompanyName = (() => {
    if (!isReportRequest) return null;
    try { return JSON.parse(localStorage.getItem("companyDescription"))?.companyname; } catch (e) { return null; }
  })();
  const mode = planDetails?.mode || localStorage.getItem("webProfilePurchaseMode") || "payg";
  const membershipPlan = planDetails?.plan;
  const summary = buildWebProfileSummary({ mode, plan: membershipPlan });

  const { data: company } = useGetCompanyQuery(
    { token, companyId: company_id },
    { skip: !company_id }
  );

  useEffect(() => {
    if (isSuccess) {
      dispatch(setUserInfo({ ...data }));
      localStorage.removeItem("slug");
      localStorage.removeItem("lastVisited");
    }
  }, [data, dispatch, isSuccess, isError, error]);

  // TIP-530 — same guard as PaymentPlans: never render a payable step without
  // a target company (deep-load with no route state and no stored id).
  // TIP-556 — report requests are exempt: their company doesn't exist yet.
  useEffect(() => {
    if (!company_id && !isReportRequest) {
      CommonToast.error("Select a business to buy its Web Profile.");
      navigate("/search", { replace: true });
    }
  }, [company_id, isReportRequest, navigate]);

  // TIP-564 — a stale createWorldboxCompany/companyDescription flag from an
  // abandoned report order must not leak into this (non-report) purchase's
  // meta_data. Guarded on isReportRequest so it never fires while an actual
  // report order is in progress (including its TIP-556 mid-flow redirect).
  useEffect(() => {
    if (!isReportRequest) {
      localStorage.removeItem("createWorldboxCompany");
      localStorage.removeItem("companyDescription");
    }
  }, [isReportRequest]);

  if (!company_id && !isReportRequest) return null;

  const copy =
    mode === "bundle"
      ? "You will be charged for Membership and the Web Profile together. The Member rate applies from this purchase forward."
      : mode === "member"
      ? `Your Member rate applies. ${PROFILE_WINDOW_DAYS} days of full claim.`
      : "One-time payment for the Web Profile. No Membership required.";

  return (
    <div className="bg-[#ebf0f3] min-h-screen">
      <Header />
      <div className="max-w-[1100px] mx-auto px-4 py-10 text-left">
        <nav className="text-f12 text-secondary mb-4 flex items-center gap-2 flex-wrap">
          <a href="/" className="hover:text-primaryText">Home</a>
          <span>›</span>
          {company?.registered_company_name && (
            <>
              <span className="text-primaryText">{company.registered_company_name}</span>
              <span>›</span>
            </>
          )}
          <span className="text-primaryText font-medium">Buy a Web Profile</span>
        </nav>
        <h1 className="text-f24 md:text-f32 font-bold text-primaryText">Buy a Web Profile</h1>
        <p className="text-secondary text-f16 mt-2 max-w-[640px]">
          Unlock {company?.registered_company_name || requestedCompanyName || "this business"}'s full profile for{" "}
          {PROFILE_WINDOW_DAYS} days.
        </p>

        <div className="mt-6 flex flex-col lg:flex-row gap-4 items-start">
          <PaymentDetailsForm
            planDetails={planDetails}
            summary={summary}
            copy={copy}
            setPaymentBreakdown={setPaymentBreakdown}
            paymentBreakdown={paymentBreakdown}
            isReportRequest={isReportRequest}
          />
          <WebProfileSummary
            company={company}
            fallbackName={requestedCompanyName}
            summary={summary}
            upsell={mode === "payg"}
            onSwitchToMembership={() =>
              navigate("/plans", { state: { company_id } })
            }
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};
